import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { caseStudies } from '@/mocks/caseStudies';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const logoUrl = 'https://storage.helloreaddy.io/project_files/8cf86b48-d09c-4ec8-816e-12bbed2b078b/1e3fa9b9-1413-4092-84fc-01303456e9e9_compressed_Biff-Digital-Logo.webp';

const caseStudySchemas = caseStudies.map((c, i) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: c.title,
  description: `${c.problem} ${c.result}`,
  author: {
    '@type': 'Person',
    name: 'BIFF Digital Strateji Ekibi',
    jobTitle: 'Dijital Pazarlama Uzmanı',
    worksFor: {
      '@type': 'Organization',
      name: 'BIFF Digital',
      url: siteUrl,
    },
  },
  publisher: {
    '@type': 'Organization',
    name: 'BIFF Digital',
    logo: { '@type': 'ImageObject', url: logoUrl },
  },
  datePublished: ['2025-03-15', '2025-07-22', '2026-01-10'][i],
  dateModified: '2026-08-07',
  about: 'Dijital Pazarlama Vaka Çalışması',
  inLanguage: 'tr',
}));

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Vaka Çalışmaları', item: `${siteUrl}/basarili-dijital-projeler` },
  ],
};

const allSchemas = {
  '@context': 'https://schema.org',
  '@graph': [
    ...caseStudySchemas,
    breadcrumbSchema,
  ],
};

export default function Vakalar() {
  useEffect(() => {
    document.title = 'BIFF Digital | Başarılı Dijital Projeler - Vaka Çalışmaları ve Referanslar';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        {/* Header */}
        <section className="section-padding bg-background-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                BOŞ LAF DEĞİL
              </p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 tracking-tighter mb-4">
                SONUÇLAR
              </h1>
              <p className="text-sm md:text-base text-foreground-400 font-medium max-w-3xl">
                Problem &rarr; Insight &rarr; Strategy &rarr; Execution &rarr; Result. İşte BIFF Digital&apos;in büyüme sisteminin gerçek müşterilerle nasıl çalıştığının kanıtı.
              </p>
            </div>

            {/* Methodology Intro */}
            <div className="glass-card rounded-2xl p-6 md:p-8 mb-10 md:mb-14">
              <h2 className="text-lg md:text-xl font-bold text-foreground-50 mb-4">
                Vaka Çalışması Metodolojimiz
              </h2>
              <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
                Her vaka çalışmamız aynı beş adımı izler: önce müşterinin karşılaştığı somut iş problemini tanımlarız (Problem), ardından verinin ortaya çıkardığı gerçek kırılma noktasını tespit ederiz (Insight). Bu içgörüye dayalı stratejiyi kurar (Strategy), uygular (Execution) ve ölçülebilir sonucu ortaya koyarız (Result).
              </p>
              <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                Bu yapı sayesinde sunduğumuz değer net, şeffaf ve tekrarlanabilir. Ankara, İstanbul ve İzmir&apos;deki müşterilerimizle tamamladığımız projelerden seçilmiş örnekleri aşağıda inceleyebilirsiniz.
              </p>
            </div>

            {/* Cases */}
            <div className="space-y-8 md:space-y-10">
              {caseStudies.map((study) => (
                <article key={study.id} className="glass-card rounded-2xl overflow-hidden">
                  {/* Header */}
                  <div className="p-6 md:p-8 border-b border-background-200/60 flex flex-wrap items-center gap-3">
                    <div className={`inline-block px-4 py-2 rounded-lg bg-gradient-to-r ${study.gradient}`}>
                      <h2 className="text-lg md:text-xl font-bold text-foreground-50">
                        <Link
                          to={`/basarili-dijital-projeler/${study.slug}`}
                          className="hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          {study.title}
                        </Link>
                      </h2>
                    </div>
                    <span className="px-3 py-1 bg-background-100 rounded-full text-xs font-bold text-foreground-400">
                      {study.category}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-6 md:p-8">
                    {/* Problem + Insight */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <h3 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                          <i className="ri-alert-line text-base" />
                          Problem
                        </h3>
                        <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                          {study.problem}
                        </p>
                      </div>
                      <div className="bg-accent-500/5 border border-accent-500/20 rounded-xl p-5">
                        <h3 className="flex items-center gap-2 text-xs md:text-sm font-bold text-accent-500 uppercase tracking-wider mb-3">
                          <i className="ri-lightbulb-line text-base" />
                          Insight
                        </h3>
                        <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                          {study.insight}
                        </p>
                      </div>
                    </div>

                    {/* Strategy + Execution */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <h3 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                          <i className="ri-compass-3-line text-base" />
                          Strategy
                        </h3>
                        <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                          {study.strategy}
                        </p>
                      </div>
                      <div>
                        <h3 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                          <i className="ri-settings-3-line text-base" />
                          Execution
                        </h3>
                        <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                          {study.execution}
                        </p>
                      </div>
                    </div>

                    {/* Result */}
                    <div className="bg-primary-500/10 border-l-4 border-primary-500 rounded-xl p-5 mb-6">
                      <h3 className="flex items-center gap-2 text-xs md:text-sm font-bold text-primary-500 uppercase tracking-wider mb-3">
                        <i className="ri-line-chart-line text-base" />
                        Result
                      </h3>
                      <p className="text-sm md:text-base text-foreground-100 font-medium leading-relaxed">
                        {study.result}
                      </p>
                    </div>

                    {/* What We Learned */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-background-100 rounded-lg flex items-center justify-center shrink-0">
                        <i className="ri-book-open-line text-lg text-secondary-500" />
                      </div>
                      <div>
                        <h3 className="text-xs md:text-sm font-bold text-secondary-500 uppercase tracking-wider mb-1">
                          What We Learned
                        </h3>
                        <p className="text-sm text-foreground-400 leading-relaxed italic">
                          &ldquo;{study.learning}&rdquo;
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-background-200/60">
                      <Link
                        to={`/basarili-dijital-projeler/${study.slug}`}
                        className="inline-flex items-center gap-2 text-sm font-bold text-primary-500 hover:text-primary-600 transition-colors uppercase tracking-wider cursor-pointer"
                      >
                        Vakanın Tamamını İncele
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-6 md:p-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                Sen de Başarı Hikayenin Parçası Ol
              </h2>
              <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto">
                Markan için benzer sonuçlar elde etmek ister misin? Hemen iletişime geç, sana özel stratejiyi birlikte oluşturalım.
              </p>
              <a href="/#contact" className="btn-primary">
                Ücretsiz Danışmanlık
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}